/**
 * HeaderRobot — the little robot sitting on the header's bottom edge.
 *
 * It types at its laptop on a loop. Call `look()` (the shell does, on any
 * header click) and it stops, turns to face the viewer, holds for a moment,
 * then turns back and carries on coding. It also walks along the header:
 * `step` places it further right for each nav page (see Props).
 *
 * ── Why a sprite sheet and not the <video> it came from ───────────────────
 * The source clip has a white background, and the header is translucent over
 * a dark canvas — so it needs real transparency. Alpha video means shipping
 * two encodes (VP9 for Chrome, HEVC for Safari) and seeking a <video> is not
 * frame-accurate, which matters when a click has to cut to "look up" NOW.
 * A sheet of pre-cut frames is one file, works everywhere, and a frame is
 * just a background-position.
 *
 * Sheet: public/robo-sheet.webp — 57 frames at 12fps, 8 per row.
 */
import * as React from 'react';
import { asset } from './asset';
import { prefersReducedMotion } from './motion';

const SHEET = asset('robo-sheet.webp');
const COLS = 8;
const ROWS = 8;

/** Frame ranges in the sheet. */
const CODE = { from: 0, to: 23 };   // side-on, typing (includes a blink)
const TURN = { from: 24, to: 38 };  // head and laptop swing round to the viewer
const FACE = { from: 39, to: 56 };  // facing the viewer

const CODE_FPS = 12;
const TURN_FPS = 30; // the turn is a reaction, so it is quicker than the idle
const HOLD_MS = 1300;

/** Inline style that shows one frame of the sheet in an element of any size. */
export function spriteStyle(frame: number): React.CSSProperties {
  return {
    backgroundImage: `url(${SHEET})`,
    backgroundSize: `${COLS * 100}% ${ROWS * 100}%`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: `${((frame % COLS) / (COLS - 1)) * 100}% ${(Math.floor(frame / COLS) / (ROWS - 1)) * 100}%`,
  };
}

export type HeaderRobotHandle = { look: () => void };

type Phase = 'code' | 'turn' | 'face' | 'return';

type Props = {
  className?: string;
  /**
   * How many steps to the right it sits: 0 on the home page, then one more per
   * nav item. It stays there, and steps back when you go back. The step length
   * is the `--robo-step` CSS variable, so the shell can shorten it on phones.
   */
  step?: number;
  /** Pressing the robot (it opens the chat). */
  onClick?: () => void;
  /** Rendered inside the robot, so it travels with it — the "say hi" bubble. */
  children?: React.ReactNode;
};

export const HeaderRobot = React.forwardRef<HeaderRobotHandle, Props>(
  function HeaderRobot({ className, step = 0, onClick, children }, ref) {
    const el = React.useRef<HTMLButtonElement>(null);
    const state = React.useRef({ phase: 'code' as Phase, frame: 0, dir: 1, holdUntil: 0 });

    const paint = React.useCallback((frame: number) => {
      const node = el.current;
      if (!node) return;
      const x = ((frame % COLS) / (COLS - 1)) * 100;
      const y = (Math.floor(frame / COLS) / (ROWS - 1)) * 100;
      node.style.backgroundPosition = `${x}% ${y}%`;
    }, []);

    React.useImperativeHandle(ref, () => ({
      look() {
        const s = state.current;
        // Already facing the viewer: just keep looking a little longer.
        if (s.phase === 'face') {
          s.holdUntil = performance.now() + HOLD_MS;
          return;
        }
        if (s.phase === 'code') s.frame = TURN.from;
        s.phase = 'turn';
        s.dir = 1;
      },
    }), []);

    React.useEffect(() => {
      const reduced = prefersReducedMotion();
      const s = state.current;
      let raf = 0;
      let last = performance.now();
      let acc = 0;

      // Drop in from above and settle onto the header edge.
      if (!reduced) {
        el.current?.animate(
          [
            { transform: 'translateY(-120%)' },
            { transform: 'translateY(6%)', offset: 0.7 },
            { transform: 'translateY(0)' },
          ],
          { duration: 700, delay: 250, easing: 'cubic-bezier(.3,1.3,.5,1)', fill: 'backwards' },
        );
      }

      const step = (now: number) => {
        switch (s.phase) {
          case 'code':
            // Ping-pong, so the loop has no visible seam.
            if (reduced) break;
            if (s.frame >= CODE.to) s.dir = -1;
            else if (s.frame <= CODE.from) s.dir = 1;
            s.frame += s.dir;
            break;
          case 'turn':
            if (s.frame >= FACE.from) {
              s.phase = 'face';
              s.dir = 1;
              s.holdUntil = now + HOLD_MS;
            } else s.frame += 1;
            break;
          case 'face':
            if (now >= s.holdUntil) {
              s.phase = 'return';
              s.frame = FACE.from;
              break;
            }
            if (reduced) break;
            if (s.frame >= FACE.to) s.dir = -1;
            else if (s.frame <= FACE.from) s.dir = 1;
            s.frame += s.dir;
            break;
          case 'return':
            if (s.frame <= TURN.from) {
              s.phase = 'code';
              s.frame = CODE.to;
              s.dir = -1;
            } else s.frame -= 1;
            break;
        }
        paint(s.frame);
      };

      const loop = (now: number) => {
        const fps = s.phase === 'turn' || s.phase === 'return' ? TURN_FPS : CODE_FPS;
        acc += Math.min(now - last, 250); // a backgrounded tab must not fast-forward
        last = now;
        if (acc >= 1000 / fps) {
          acc = 0;
          step(now);
        }
        raf = requestAnimationFrame(loop);
      };

      paint(s.frame);
      raf = requestAnimationFrame(loop);
      return () => cancelAnimationFrame(raf);
    }, [paint]);

    return (
      <button
        ref={el}
        type="button"
        aria-label="Chat with Charlie"
        onClick={onClick}
        className={className}
        style={{
          backgroundImage: `url(${SHEET})`,
          backgroundSize: `${COLS * 100}% ${ROWS * 100}%`,
          backgroundRepeat: 'no-repeat',
          // `translate`, not `transform`: the drop-in animates transform.
          translate: `calc(var(--robo-step, 18px) * ${step}) 0`,
          transition: 'translate 450ms cubic-bezier(.3,1.2,.5,1)',
          // A brand-violet glow, so it sits in the page instead of on top of it.
          filter: 'drop-shadow(0 3px 8px rgb(94 25 236 / .3))',
        }}
      >
        {children}
      </button>
    );
  },
);
